import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { Loader2, Eye, Warehouse } from 'lucide-react';
import { DiscoveryResultsTable } from '../components/DiscoveryResultsTable';
import { QueueSelectedBar } from '../components/QueueSelectedBar';

interface OwnStockFields {
  price: string;
  qty: string;
  cost: string;
}

function ManualSubTab({ sites = [], condition = 'new' }: { sites?: string[]; condition?: 'new' | 'refurbished' }) {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const [asinInput, setAsinInput] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAsins, setSelectedAsins] = useState<Set<string>>(new Set());
  const [enqueuing, setEnqueuing] = useState(false);
  // Modo stock propio (spec 2026-07-08): publicar aunque Amazon no tenga stock,
  // vendiendo unidades del depósito. Precio y cantidad manuales por ASIN.
  const [ownStock, setOwnStock] = useState(false);
  const [ownStockFields, setOwnStockFields] = useState<Record<string, OwnStockFields>>({});
  const [ownStockError, setOwnStockError] = useState<string | null>(null);

  const handlePreview = async () => {
    if (!asinInput.trim()) return;
    setLoading(true);
    setResults([]);
    try {
      const asins = asinInput
        .split(/[\n,]+/)
        .map(a => a.trim().toUpperCase())
        .filter(a => /^[A-Z0-9]{10}$/.test(a));
      const res = await api.post<any>('/publisher/discover/preview', { asins, seller_id: sellerId });
      if (res.success && res.data) {
        setResults(Array.isArray(res.data) ? res.data : res.data.items || []);
      }
    } catch (err) {
      console.error('Preview failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleAsin = (asin: string) => {
    setSelectedAsins(prev => {
      const next = new Set(prev);
      if (next.has(asin)) next.delete(asin); else next.add(asin);
      return next;
    });
  };

  const setField = (asin: string, field: keyof OwnStockFields, value: string) => {
    setOwnStockFields(prev => ({
      ...prev,
      [asin]: { ...(prev[asin] ?? { price: '', qty: '', cost: '' }), [field]: value },
    }));
  };

  const handleQueue = async () => {
    if (selectedAsins.size === 0) return;
    if (sites.length === 0) {
      console.warn('[publisher] No active sites configured for this seller — abort enqueue');
      return;
    }
    setOwnStockError(null);

    let own_stock_items: Record<string, { price: number; qty: number; cost?: number }> | undefined;
    if (ownStock) {
      own_stock_items = {};
      for (const asin of selectedAsins) {
        const f = ownStockFields[asin];
        const price = parseFloat(f?.price || '');
        const qty = parseInt(f?.qty || '', 10);
        if (!(price > 0) || !(qty >= 1)) {
          setOwnStockError(`Falta precio (>0) o cantidad (>=1) para ${asin}`);
          return;
        }
        const cost = parseFloat(f?.cost || '');
        own_stock_items[asin] = { price, qty, ...(cost >= 0 && f?.cost !== '' ? { cost } : {}) };
      }
    }

    setEnqueuing(true);
    try {
      await api.post('/publisher/queue/enqueue', {
        asins: Array.from(selectedAsins),
        sites,
        seller_id: sellerId,
        condition,
        ...(own_stock_items ? { own_stock_items } : {}),
      });
      setSelectedAsins(new Set());
      setOwnStockFields({});
    } catch (err) {
      console.error('Enqueue failed:', err);
    } finally {
      setEnqueuing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
        <textarea
          value={asinInput}
          onChange={(e) => setAsinInput(e.target.value)}
          placeholder={t('pub.feed.pasteAsins')}
          rows={3}
          className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] font-mono resize-none mb-3"
        />
        <div className="flex items-center gap-4">
          <button
            onClick={handlePreview}
            disabled={loading || !asinInput.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            {t('pub.feed.preview')}
          </button>
          <label className="flex items-center gap-2 text-sm text-[var(--color-text)] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={ownStock}
              onChange={(e) => setOwnStock(e.target.checked)}
              className="accent-[var(--color-primary)]"
            />
            <Warehouse size={14} className="text-[var(--color-text-secondary)]" />
            Stock propio (publicar aunque Amazon no tenga stock)
          </label>
        </div>
        {ownStock && (
          <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
            Vas a vender unidades de tu depósito: el aviso se publica con tu precio y cantidad,
            el Monitor de Precios no lo toca y las ventas descuentan del inventario interno.
          </p>
        )}
      </div>

      <DiscoveryResultsTable
        items={results}
        selectedAsins={selectedAsins}
        toggleAsin={toggleAsin}
        loading={loading}
        emptyMessage={t('pub.feed.pasteAsins')}
        allowQueueDuplicates={ownStock}
      />

      {ownStock && selectedAsins.size > 0 && (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
          <h4 className="text-sm font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
            <Warehouse size={14} /> Stock propio — precio y cantidad por producto
          </h4>
          <div className="space-y-2">
            {Array.from(selectedAsins).map((asin) => {
              const f = ownStockFields[asin] || { price: '', qty: '', cost: '' };
              const title = results.find((r) => r.asin === asin)?.title || '';
              return (
                <div key={asin} className="flex items-center gap-3 text-sm">
                  <span className="font-mono w-28 shrink-0 text-[var(--color-text)]">{asin}</span>
                  <span className="flex-1 truncate text-xs text-[var(--color-text-secondary)]" title={title}>{title}</span>
                  <input
                    type="number" min="0.01" step="0.01" placeholder="Precio USD *"
                    value={f.price}
                    onChange={(e) => setField(asin, 'price', e.target.value)}
                    className="w-28 px-2 py-1 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]"
                  />
                  <input
                    type="number" min="1" step="1" placeholder="Cantidad *"
                    value={f.qty}
                    onChange={(e) => setField(asin, 'qty', e.target.value)}
                    className="w-24 px-2 py-1 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]"
                  />
                  <input
                    type="number" min="0" step="0.01" placeholder="Costo unit."
                    value={f.cost}
                    onChange={(e) => setField(asin, 'cost', e.target.value)}
                    className="w-24 px-2 py-1 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]"
                  />
                </div>
              );
            })}
          </div>
          {ownStockError && (
            <p className="mt-2 text-xs text-red-500">{ownStockError}</p>
          )}
        </div>
      )}

      <QueueSelectedBar count={selectedAsins.size} onQueue={handleQueue} loading={enqueuing} />
    </div>
  );
}

export { ManualSubTab };
